import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
  getCommercialProgram,
  getCommitmentNotes,
  getProgramCommitments,
  getProgramVersions,
} from 'common/src/api/commercialProgram';
import { getReceiversByExporter } from 'common/src/api/exporterReceivers';
import { getFruticReceiverMapping } from 'common/src/api/frutic';
import { getCommercialPlanShipments } from 'common/src/api/shipments';
import useUserStore from 'common/src/stores/useUserStore';
import useFilterStore from 'common/src/stores/useFiltersStore';
import useDataStore from 'common/src/stores/useDataStore';
import {
  getSeasonCutoffIndex,
  getSeasonWeeksForSpecies,
} from 'common/src/utils/seasonWeeks';
import type { ProgramShipment } from 'common/src/utils/commercialProgram';
import type {
  CommercialProgram,
  CommercialProgramVersion,
  Commitment,
  CommitmentNote,
  ExporterReceiver,
  ReceiverMapping,
} from 'common/src/utils/types';
import { useTranslation } from 'common/src/hooks/useTranslation';

/** Todo lo que necesita el Programa Comercial de la temporada y especie elegidas. */
export function useCommercialProgram() {
  const { t } = useTranslation('commitments');
  const { userProfile } = useUserStore();
  const { selectedSeason } = useFilterStore();
  const { selectedSpecies } = useDataStore();
  const exporterId = userProfile?.exporter_id;
  const speciesId = selectedSpecies?.id;

  const [loading, setLoading] = useState(true);
  const [program, setProgram] = useState<CommercialProgram | null>(null);
  const [rows, setRows] = useState<Commitment[]>([]);
  const [versions, setVersions] = useState<CommercialProgramVersion[]>([]);
  const [notes, setNotes] = useState<CommitmentNote[]>([]);
  const [receivers, setReceivers] = useState<ExporterReceiver[]>([]);
  const [shipments, setShipments] = useState<ProgramShipment[]>([]);
  const [mappings, setMappings] = useState<ReceiverMapping[]>([]);

  const weeks = useMemo(
    () => getSeasonWeeksForSpecies(speciesId, selectedSeason),
    [speciesId, selectedSeason]
  );
  const cutoffIndex = useMemo(
    () => getSeasonCutoffIndex({ weeks, selectedSeason }),
    [weeks, selectedSeason]
  );

  /**
   * Compromisos, versiones y notas (lo que cambia al editar). isStale descarta
   * la respuesta si mientras tanto se cambió de temporada o especie.
   */
  const reloadProgramData = useCallback(
    async (
      current: CommercialProgram | null,
      isStale: () => boolean = () => false
    ) => {
      if (!current) {
        if (isStale()) return;
        setRows([]);
        setVersions([]);
        setNotes([]);
        return;
      }
      const [c, v, n] = await Promise.all([
        getProgramCommitments(current.id),
        getProgramVersions(current.id),
        getCommitmentNotes(current.id),
      ]);
      if (isStale()) return;
      setRows(c);
      setVersions(v);
      setNotes(n);
    },
    []
  );

  const reloadProgram = useCallback(
    async (isStale: () => boolean = () => false) => {
      if (!exporterId || !speciesId) return;
      const current = await getCommercialProgram(
        exporterId,
        selectedSeason,
        speciesId
      );
      if (isStale()) return;
      setProgram(current);
      await reloadProgramData(current, isStale);
    },
    [exporterId, selectedSeason, speciesId, reloadProgramData]
  );

  useEffect(() => {
    let active = true;
    const isStale = () => !active;
    const load = async () => {
      if (!exporterId || !speciesId) return;
      setLoading(true);
      try {
        const [r, s, m] = await Promise.all([
          getReceiversByExporter(exporterId),
          getCommercialPlanShipments(exporterId, selectedSeason, speciesId),
          getFruticReceiverMapping(userProfile),
          reloadProgram(isStale),
        ]);
        if (isStale()) return;
        setReceivers(r);
        setShipments((s as ProgramShipment[]) || []);
        setMappings((m as unknown as ReceiverMapping[]) || []);
      } catch (e: unknown) {
        if (isStale()) return;
        const message = e instanceof Error ? e.message : String(e);
        toast.error(t('Error cargando datos: {{error}}', { error: message }));
      } finally {
        if (!isStale()) setLoading(false);
      }
    };
    load();
    return () => {
      active = false;
    };
    // Recargar los despachos propios al cambiar de exportadora, temporada o especie.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exporterId, selectedSeason, speciesId, userProfile?.exporter?.hasDemo]);

  /** Cliente del despacho: la FK si existe, si no el mapeo de nombres. */
  const receiverIdOf = useMemo(() => {
    const byName = new Map<string, number>();
    mappings.forEach((m) =>
      byName.set(
        m.frutic_receiver_name.trim().toUpperCase(),
        m.exporter_receiver_id
      )
    );
    return (shipment: ProgramShipment): number | null => {
      const explicit = Number(shipment.exporter_receiver_id);
      if (Number.isFinite(explicit) && explicit > 0) return explicit;
      return byName.get((shipment.receiver || '').trim().toUpperCase()) || null;
    };
  }, [mappings]);

  /** Suma un recibidor recién creado sin volver a cargar todo. */
  const addReceiver = useCallback((receiver: ExporterReceiver) => {
    setReceivers((prev) =>
      prev.some((r) => r.id === receiver.id)
        ? prev
        : [...prev, receiver].sort((a, b) => a.name.localeCompare(b.name))
    );
  }, []);

  /** Clientes de despachos que no están configurados como recibidores. */
  const unknownReceivers = useMemo(
    () =>
      Array.from(
        new Set(
          shipments
            .filter((s) => !receiverIdOf(s) && (s.receiver || '').trim())
            .map((s) => (s.receiver || '').trim())
        )
      ),
    [shipments, receiverIdOf]
  );

  return {
    loading,
    program,
    setProgram,
    rows,
    versions,
    notes,
    receivers,
    addReceiver,
    shipments,
    receiverIdOf,
    receiverMappings: mappings,
    unknownReceivers,
    weeks,
    cutoffIndex,
    season: selectedSeason,
    species: selectedSpecies,
    userProfile,
    reloadProgram: () => reloadProgram(),
    reloadProgramData: () => reloadProgramData(program),
  };
}

export type CommercialProgramState = ReturnType<typeof useCommercialProgram>;
